import { useCallback, useEffect, useRef, useState } from 'react';
import { consola } from 'consola';
import { useSocketsContext } from '@/shared/libs/sockets/sockets.context';
import { SocketEvent } from '@/shared/libs/sockets/enums/socket-event';
import type { SupportMessagePayload } from '@/shared/libs/sockets/events/support-income-message.output';
import { useNavigate } from 'react-router';
import { useAuthStore } from '@/state/auth.state';
import { useDocumentVisibility } from '@mantine/hooks';
import { toast } from 'react-toastify';

export function useBrowserNotifications() {
    const ref = useRef<{
        notifications: Map<string, Notification>;
    }>({
        notifications: new Map(),
    });
    const navigate = useNavigate();
    const [ isNotificationsAvailable, setIsNotificationsAvailable ] = useState(false);
    
    useEffect(() => {
        if (!("Notification" in window)) {
            consola.error('Browser Notifications not supported.');
            return;
        }
        Notification.requestPermission().then((permission) => {
            setIsNotificationsAvailable(permission === "granted");
        });
    }, []);
    
    const showNotification = useCallback((params: {
        notificationId: string,
        title: string;
        body: string;
        path?: string;
    }) => {
        if (!isNotificationsAvailable) {
            consola.error('Notification permission was not granted.');
            return;
        }
        const notification = new Notification(params.title, {
            body: params.body,
        });
        notification.onclick = (event) => {
            event.preventDefault(); // prevent the browser from focusing the Notification's tab
            window.focus();
            notification.close();
            if (params.path) {
                navigate(params.path);
            }
        };
        ref.current.notifications.set(params.notificationId, notification);
    }, [ isNotificationsAvailable, navigate ]);
    
    const closeNotification = useCallback((notificationId: string) => {
        if (!isNotificationsAvailable) {
            consola.error('Notification permission was not granted.');
            return;
        }
        const notification = ref.current.notifications.get(notificationId);
        
        if (notification) {
            notification.close();
            ref.current.notifications.delete(notificationId);
        }
        
    }, [ isNotificationsAvailable ]);
    
    return {
        showNotification,
        closeNotification,
    }
}

export function useSocketBrowserNotifications() {
    const { subscribe, unsubscribe } = useSocketsContext();
    const { showNotification } = useBrowserNotifications();
    const documentState = useDocumentVisibility();
    const authStore = useAuthStore();
    
    useEffect(() => {
        const newSupportMessageHandler = (payload: SupportMessagePayload) => {
            if (documentState === 'hidden') {
                showNotification({
                    notificationId: `new-message-${ payload?.fromTgUserId }`,
                    title: 'Сообщение в поддержке',
                    body: `${ payload?.fromTgUserId }: ${ payload?.message }`,
                    path: `/support/${ payload?.fromTgUserId }`,
                })
            } else {
                toast.info((
                    <>
                        <b>Сообщение в поддержке</b>
                        { payload?.fromTgUserId }: { payload?.message }
                    </>
                ));
            }
        }
        if (authStore.isAuthenticated) {
            subscribe(SocketEvent.SUPPORT_MESSAGE_INCOME, newSupportMessageHandler);
        }
        
        return () => {
            if (authStore.isAuthenticated) {
                unsubscribe(SocketEvent.SUPPORT_MESSAGE_INCOME, newSupportMessageHandler)
            }
        }
    }, [ authStore.isAuthenticated, documentState, showNotification, subscribe, unsubscribe ]);
}